
import {
  Mixins,
  Component,
  Provide,
  Prop,
  Watch
} from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";

// Mixins
import Context from "@/mixins/context";
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IAccount } from "@/models/accounts";
import {
  IAffiliate,
  IAffiliateBalance,
  IAffiliateLink
} from "@/models/affiliate";
import {
  IAffiliateProvider,
  IAffiliateProviderMethods
} from "@/models/providers/affiliate";
import { IClient } from "@/models/clients";
import { IProvider } from "@/models/providers";
import _ from "@/boot/lodash";
import { BrandConfigKeys } from "@/data/constants";

@Component({
  computed: {
    ...mapState({
      affiliateScope(state: IState, getters) {
        return getters[`data/${DataModules.AFFILIATES}/scope`](this.accountId);
      },
      affiliate(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.AFFILIATES,
            scope: this.affiliateScope
          }) || {}
        );
      },
      affiliateBalanceScope(state: IState, getters) {
        return [
          getters[`data/${DataModules.AFFILIATES}/scope`](this.accountId),
          "balance"
        ].join("_");
      },
      affiliateBalance(state, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.AFFILIATES,
          scope: this.affiliateBalanceScope
        });
      }
    })
  }
})
export default class AffiliateProvider extends Mixins(Context, RequestPromises)
  implements IProvider {
  // Map State
  public affiliate!: IAffiliate;
  public affiliateBalance!: IAffiliateBalance;
  public affiliateBalanceScope!: string;
  public affiliateScope!: string;

  // Props
  @Prop({ required: true })
  public accountId!: IAccount["id"];

  @Prop({ required: false })
  public client!: IClient;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: false })
  public handleRequestError!: boolean;

  @Prop({ default: false })
  public handleGuarding!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public guardFunctionalities = ["get_affiliate_account"];

  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) {
      this.isLoading = false;
      return;
    }

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.AFFILIATES,
      scope: this.affiliateScope,
      vm: this
    });
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.AFFILIATES,
      scope: this.affiliateBalanceScope,
      vm: this
    });
  }

  // Methods

  public async loadData({ ignoreStored = false } = {}) {
    try {
      await Promise.all([
        this.getAffiliateAccount(ignoreStored),
        this.getAffiliateBalance(ignoreStored),
        this.getAffiliateBrandSettings(ignoreStored)
      ]);
      this.hasRequestError = false;
    } catch (error) {
      const is404 = this.$store.getters["api/is404"](error);
      if (is404) return;
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public getAffiliateAccount(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.AFFILIATES}/get`, {
      accountId: this.accountId,
      scope: this.affiliateScope,
      vm: this,
      ignoreStored,
      params: {
        with_staged_imports: 1,
        with: [
          "account",
          "account.brand",
          "account.clients",
          "import.credentials",
          "import.source",
          "account.affiliate_payout_destination"
        ].join()
      }
    });
  }

  public async getAffiliateBrandSettings(ignoreStored = false) {
    try {
      return this.$store.dispatch("brand/getConfig", {
        brandId: this.client?.brand_id || this.$store.state.brand.id,
        keys: [BrandConfigKeys.AFFILIATES_DEFAULT_REDIRECT_LINK],
        ignoreStored: ignoreStored || this.$store.getters["brand/isMultibrand"]
      });
    } catch {
      // Silent catch (non-critical)
    }
  }

  public getAffiliateBalance(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.AFFILIATES}/getBalance`, {
      accountId: this.accountId,
      scope: this.affiliateBalanceScope,
      vm: this,
      ignoreStored,
      params: {
        with_staged_imports: 1
      }
    });
  }

  public async enrol() {
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.AFFILIATES}/create`, {
        accountId: this.accountId,
        scope: this.affiliateScope,
        vm: this
      });
      await this.reloadData();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public getAffiliateLink(payload) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_LINKS}/get`,
      payload
    );
  }

  public createAffiliateLink(payload) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_LINKS}/create`,
      payload
    );
  }

  public updateAffiliateLink(payload) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_LINKS}/update`,
      payload
    );
  }

  public generateLink(hash) {
    const defaultDomain = _.get(this.brand, "oauth_clients", []).find(
      i => i.default
    );
    return `${defaultDomain?.origin}/aff/${hash}`;
  }

  public async manuallyLinkReferral() {
    try {
      this.$store.dispatch(
        `data/${DataModules.CLIENTS}/openSelectClientModal`,
        {
          props: {
            title: this.$t("_action.select_affiliate_referral"),
            allowGuestClientOption: false,
            showGuestsToggle: false,
            manualFilters: {
              "filter[accounts.affiliate_referral.id|eq]": "null",
              "filter[id|neq]": this.client.id
            },
            withParams: "accounts.affiliate_referral"
          },
          events: {
            onSelect: async (client: IClient) => {
              await this.$store.dispatch(
                `data/${DataModules.AFFILIATES_REFERRALS}/linkReferrer`,
                {
                  clientId: client.id,
                  affiliateAccountId: this.affiliate.id
                }
              );

              this.$bus.$emit("refreshAffiliateReferralsTable");

              this.$store.dispatch("toast/show", {
                message: this.$t(
                  "_sentence.client_successfully_added_as_referral"
                )
              });
            }
          }
        }
      );
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }
  public openCommissionPayoutModal(config = {}) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_PAYOUTS}/openPayoutModal`,
      this.$_.merge(
        {
          parent: this,
          props: {
            accountId: this.accountId
          },
          events: {
            success: () => this.reloadData()
          }
        },
        config
      )
    );
  }

  public openAddLinkModal(affiliateLinkId: IAffiliateLink["id"] | null = null) {
    return this.$store.dispatch(
      `data/${DataModules.AFFILIATES_LINKS}/openAddLinkModal`,
      {
        parent: this,
        props: {
          affiliateLinkId
        },
        events: {
          success: () => this.$bus.$emit("refreshAffiliateLinksTable")
        }
      }
    );
  }

  // Computed

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get brand() {
    return (
      _.get(this.affiliate, "account.brand") || _.get(this.client, "brand")
    );
  }

  public get isEnrolled() {
    return !!this.accountId && !_.isEmpty(this.affiliate);
  }

  public get canGetAffiliateAccount() {
    return this.$userCan("get_affiliate_account");
  }

  public get canGetLink() {
    return this.$userCan("get_affiliate_account_link");
  }

  public get canLinkReferral() {
    return this.$userCan("create_affiliate_referral");
  }

  public get canCreateLink() {
    return this.$userCan("create_affiliate_account_link");
  }

  public get canUpdateLink() {
    return this.$userCan("update_affiliate_account_link");
  }

  public get canDeleteLink() {
    return this.$userCan("delete_affiliate_account_link");
  }

  public get balanceAvailable() {
    return this.affiliateBalance?.balance?.ALL?.amount_formatted;
  }

  public get balancePending() {
    return this.affiliateBalance?.pending_balance?.ALL?.amount_formatted;
  }

  public get balanceWithdrawn() {
    return this.affiliateBalance?.withdrawn_balance?.ALL?.amount_formatted;
  }

  public get hasPayableCommissions() {
    return !!this.affiliateBalance?.balance?.ALL?.amount;
  }

  public get isStaged() {
    return this.affiliate.staged_import;
  }

  // Watchers

  @Watch("isLoading")
  public onIsLoadingChange(value) {
    this.$emit("isLoading", value);
  }

  @Watch("isEnrolled")
  public onIsEnrolledChange(value) {
    this.$emit("isEnrolled", value);
  }

  @Watch("isProcessing")
  public onIsProcessingChange(value) {
    this.$emit("isProcessing", value);
  }

  @Watch("contentIsAvailable")
  public onContentIsAvailableChange(value) {
    this.$emit("contentIsAvailable", value);
  }

  // Render

  public render(h: CreateElement) {
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false, ifClient: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $affiliateData: this.providerData(),
        $affiliateMethods: this.providerMethods
      })
    );
  }

  @Provide("affiliateProvider")
  public providerData(): IAffiliateProvider {
    return {
      accountId: this.accountId,
      affiliate: this.affiliate,
      balanceAvailable: this.balanceAvailable,
      balancePending: this.balancePending,
      balanceWithdrawn: this.balanceWithdrawn,
      brand: this.brand,
      canCreateLink: this.canCreateLink,
      canDeleteLink: this.canDeleteLink,
      canGetAffiliateAccount: this.canGetAffiliateAccount,
      canGetLink: this.canGetLink,
      canLinkReferral: this.canLinkReferral,
      canUpdateLink: this.canUpdateLink,
      contentIsAvailable: this.contentIsAvailable,
      guardFunctionalities: this.guardFunctionalities,
      hasPayableCommissions: this.hasPayableCommissions,
      hasRequestError: this.hasRequestError,
      isEnrolled: this.isEnrolled,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      isStaged: this.isStaged
    };
  }

  @Provide("affiliateProviderMethods")
  public get providerMethods(): IAffiliateProviderMethods {
    return {
      createAffiliateLink: this.createAffiliateLink,
      enrol: this.enrol,
      generateLink: this.generateLink,
      getAffiliateLink: this.getAffiliateLink,
      manuallyLinkReferral: this.manuallyLinkReferral,
      openAddLinkModal: this.openAddLinkModal,
      openCommissionPayoutModal: this.openCommissionPayoutModal,
      reloadData: this.reloadData,
      updateAffiliateLink: this.updateAffiliateLink
    };
  }
}
